import {
  ConflictException,
  ForbiddenException,
  Injectable,
  Logger,
  NotFoundException,
  UnauthorizedException,
  BadRequestException,
  Optional,
} from '@nestjs/common';
import * as bcrypt from 'bcrypt';
import * as jwt from 'jsonwebtoken';
import { createHash, randomBytes } from 'crypto';
import {
  ApplicationStatusResponse,
  AuthResponse,
  AuthUser,
  CheckEmailStatusResponse,
  MyStoresResponse,
  ForgotPasswordRequest,
  LoginRequest,
  MerchantApprovalStatus,
  MerchantPlan,
  RegisterMerchantRequest,
  RegisterMerchantResponse,
  RegisterRequest,
  RegisterResponse,
  ResendVerificationRequest,
  ResetPasswordRequest,
  UserRole,
  VerifyEmailRequest,
} from '@cart/shared-types';
import { PrismaService } from '@cart/api-data-access';
import { AuditService } from '../audit/audit.service';
import { EmailService } from '../notifications/email.service';
import { AuthRepository, StoredUser } from './auth.repository';
import { GoogleProfile } from './google-oauth.service';
import { resolveFrontendOrigin } from './frontend-origin.util';
import { MERCHANT_REVIEW_NOTIFICATION_EMAILS } from '../notifications/admin-notification-emails.constants';
import { StoreContextService, DEFAULT_STORE_ID } from '../tenancy/store-context.service';
import { SessionService } from './session.service';
import { StoreProvisioningService } from '../merchant/store-provisioning.service';

interface AuthTokenPayload {
  sub: string;
  email: string;
  role: string;
  // Merchant/platform tokens only — chat-service's AgentJwtGuard reads this
  // to resolve which store an agent is acting for. Customer tokens never
  // carry it: identity is global (one User row across every store, see
  // CustomerStoreMembership), so there's no single storeId a customer
  // token could correctly represent.
  storeId?: string;
}

/**
 * Access-token lifetime. Configurable so tests can compress an hour into a
 * minute and exercise a real expiry, rather than editing this file and
 * risking a short TTL reaching production — which is exactly what nearly
 * happened while debugging by hand.
 *
 * Any jsonwebtoken duration string: 15m, 1h, 7d.
 */
const ACCESS_TOKEN_TTL = process.env.AUTH_ACCESS_TOKEN_TTL ?? '1h';

const RESET_TOKEN_TTL_MS = 60 * 60 * 1000;
const VERIFICATION_TOKEN_TTL_MS = 24 * 60 * 60 * 1000;

@Injectable()
export class AuthService {
  private readonly logger = new Logger(AuthService.name);
  private readonly jwtSecret = process.env.AUTH_JWT_SECRET ?? 'local-development-auth-secret';

  constructor(
    private readonly authRepository: AuthRepository,
    private readonly prisma: PrismaService,
    @Optional()
    private readonly auditService?: AuditService,
    @Optional()
    private readonly emailService?: EmailService,
    @Optional()
    private readonly storeContext?: StoreContextService,
    // Optional so the many specs that construct AuthService directly keep
    // working; session revocation is skipped when it is absent.
    @Optional()
    private readonly sessionService?: SessionService,
    // Optional like the rest — the specs build AuthService bare. Without it a
    // merchant simply creates their first store by hand, as before.
    @Optional()
    private readonly storeProvisioning?: StoreProvisioningService
  ) {}

  // A customer logging in (or registering) at a store they don't already
  // have a CustomerStoreMembership row for gets one created automatically —
  // this is how "joining" a new store works (no separate invite/join
  // endpoint), safely gated behind the password check login()/register()
  // already do. Same reactivate-or-create shape as
  // MerchantService.addStaff() for StoreStaff. Informational only, not an
  // access gate — see the model's own doc comment in schema.prisma. Fail
  // soft like AuditService/EmailService elsewhere in this file: this is a
  // side effect powering the "my stores" list, never a reason to fail the
  // actual login/registration.
  private async ensureMembership(userId: string, storeId: string): Promise<void> {
    try {
      await this.ensureMembershipOrThrow(userId, storeId);
    } catch (error) {
      this.logger.warn(`Failed to record store membership for user ${userId} at store ${storeId}: ${(error as Error).message}`);
    }
  }

  private async ensureMembershipOrThrow(userId: string, storeId: string): Promise<void> {
    const existing = await this.prisma.customerStoreMembership.findFirst({ where: { storeId, userId } });

    if (existing) {
      // Always touched, not just when reactivating — updatedAt doubles as
      // "last logged in at this store", which the picker's default
      // selection (myStores(), sorted by it) depends on.
      await this.prisma.customerStoreMembership.update({
        where: { id: existing.id },
        data: { isActive: true, updatedBy: userId },
      });
      return;
    }

    await this.prisma.customerStoreMembership.create({
      data: { storeId, userId, role: 'customer', createdBy: userId, updatedBy: userId },
    });
  }

  private ambientStoreId(): string {
    return this.storeContext?.storeId() ?? DEFAULT_STORE_ID;
  }

  async register(input: RegisterRequest): Promise<RegisterResponse> {
    this.validateRegister(input);

    const email = input.email.trim().toLowerCase();
    const existing = await this.authRepository.findByEmail(email);

    if (existing) {
      // An unverified signup from a prior attempt shouldn't permanently
      // lock this email out; resend the link rather than block. Only for
      // their own still-pending customer signup — never for an
      // already-verified account or a different role. A verified identity
      // registering again (at this store or a new one) should log in
      // instead — login() is what creates a CustomerStoreMembership for a
      // new store, not registration (see AuthService.ensureMembership).
      if (existing.role === 'customer' && !existing.emailVerifiedAt) {
        await this.issueVerificationEmail(existing, input.returnOrigin);
        return { email: existing.email };
      }
      throw new ConflictException('Email is already registered');
    }

    const user = await this.authRepository.create({
      email,
      name: input.name.trim(),
      passwordHash: await bcrypt.hash(input.password, 10),
      role: 'customer',
    });

    await this.ensureMembership(user.id, this.ambientStoreId());

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'create',
      changedColumns: ['email', 'name', 'role'],
      newValue: this.toPublicUser(user),
      changedBy: user.id,
      source: 'AuthService.register',
    });

    await this.emailService?.sendTemplated('welcome_registration', user.email, {
      customerName: user.name,
    });

    // New registrations start unverified (see migration 018) — login is
    // hard-blocked until this link is clicked, so no accessToken is issued
    // here the way the other auth flows do.
    await this.issueVerificationEmail(user, input.returnOrigin);

    return { email: user.email };
  }

  // Auto-approval is derived from the plan the merchant self-selected at
  // signup (free = queued for review, basic/premium = instant) — confirmed
  // product decision, see plan doc. Both paid tiers skip review, not just
  // the top one.
  private static readonly AUTO_APPROVE_PLANS = new Set(['basic', 'premium']);

  async registerMerchant(input: RegisterMerchantRequest): Promise<RegisterMerchantResponse> {
    this.validateRegisterMerchant(input);

    const email = input.email.trim().toLowerCase();
    const existing = await this.authRepository.findByEmail(email);

    if (existing) {
      // A merchant who registered but never verified (lost the email,
      // ignored it, came back the next day) must have a way back in
      // without contacting support — resend the link instead of leaving
      // them permanently stuck behind "email already exists". Only for
      // their own still-pending merchant signup, never for an
      // already-verified account or one registered under a different role
      // (registering here can't "convert" an existing customer into a
      // merchant, and a fully verified merchant genuinely is a duplicate).
      if (existing.role === 'merchant' && !existing.emailVerifiedAt) {
        await this.issueVerificationEmail(existing, input.returnOrigin);
        return { email: existing.email };
      }
      throw new ConflictException('Email is already registered');
    }

    const autoApprove = AuthService.AUTO_APPROVE_PLANS.has(input.plan);
    const slug = await this.uniqueOrgSlug(input.businessName);

    const org = await this.prisma.organization.create({
      data: {
        name: input.businessName.trim(),
        slug,
        email,
        phone: input.phone.trim(),
        businessType: input.businessType.trim(),
        gstin: input.gstin?.trim() || null,
        city: input.city?.trim() || null,
        state: input.state?.trim() || null,
        plan: input.plan,
        approvalStatus: autoApprove ? 'approved' : 'pending',
        approvedAt: autoApprove ? new Date() : null,
        approvedBy: autoApprove ? 'system:auto-plan' : null,
        createdBy: 'system:merchant-registration',
        updatedBy: 'system:merchant-registration',
      },
    });

    const user = await this.authRepository.create({
      email,
      name: input.name.trim(),
      passwordHash: await bcrypt.hash(input.password, 10),
      role: 'merchant',
      organizationId: org.id,
    });

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'create',
      changedColumns: ['email', 'name', 'role', 'organizationId'],
      newValue: this.toPublicUser(user),
      changedBy: user.id,
      source: 'AuthService.registerMerchant',
    });

    // The merchant's first store, created with the account.
    //
    // Registration used to create only an Organization and a User, leaving the
    // merchant to create a store themselves. When that step was never completed
    // — or failed — they were left with an account that could log in and do
    // nothing: AdminAccessGuard refused every admin screen because there was no
    // store to be staff of. One paid signup reached us in exactly that state.
    //
    // Failing soft is deliberate. A store that could not be provisioned is
    // recoverable (My Stores lets them create one); an account that was never
    // created because store provisioning threw is not.
    await this.provisionFirstStore(org.id, user.id, input);

    // Same hard-block as customer registration — must verify before any
    // login/approval-status check means anything.
    await this.issueVerificationEmail(user, input.returnOrigin);

    await this.emailService?.sendTemplated(
      autoApprove ? 'merchant_approved' : 'merchant_registration_pending',
      user.email,
      { customerName: user.name, businessName: org.name, plan: org.plan }
    );

    // Auto-approved signups need no one to act on them — only a submission
    // that's actually queued for review needs a human to log in and
    // approve/reject it. Recipients are a hardcoded list (see the constants
    // file) rather than a DB-backed setting, per explicit product direction.
    if (!autoApprove) {
      const reviewUrl = `${resolveFrontendOrigin(input.returnOrigin)}/platform/merchants?filter=pending`;
      await Promise.all(
        MERCHANT_REVIEW_NOTIFICATION_EMAILS.map((to) =>
          this.emailService?.sendTemplated('merchant_application_received', to, {
            businessName: org.name,
            contactName: user.name,
            contactEmail: user.email,
            contactPhone: org.phone ?? '',
            plan: org.plan,
            reviewUrl,
          })
        )
      );
    }

    return { email: user.email };
  }

  // Live check queried on blur of the email field, before the form is even
  // submitted — same rules registerMerchant()/register() apply when they
  // actually see this email, just surfaced early so the page can offer a
  // "resend verification" link immediately instead of only after a full
  // submit-and-409 round trip. Deliberately reveals registration state (same
  // enumeration tradeoff already made elsewhere in this file, e.g.
  // forgotPassword's 404) — this is a UX aid on the registration path, not a
  // credential check.
  async checkEmailStatus(email: string, role: UserRole): Promise<CheckEmailStatusResponse> {
    const existing = await this.authRepository.findByEmail(email.trim().toLowerCase());

    if (!existing) return { status: 'available' };
    if (existing.role === role && !existing.emailVerifiedAt) return { status: 'unverified' };
    return { status: 'taken' };
  }

  // Authenticated — backs the sp-account.forgestack.in "which store"
  // picker, shown after a global login. Lists the stores this identity
  // actually has a CustomerStoreMembership at (see that model's doc
  // comment) rather than doing an email lookup, since login no longer
  // needs to know which store you want before authenticating you.
  async myStores(userId: string): Promise<MyStoresResponse> {
    const memberships = await this.prisma.customerStoreMembership.findMany({
      where: { userId, isActive: true },
      orderBy: { updatedAt: 'desc' },
      select: {
        role: true,
        store: {
          select: {
            name: true,
            organizationId: true,
            organization: { select: { name: true } },
            domains: { where: { isPrimary: true, isActive: true }, select: { domain: true } },
          },
        },
      },
    });

    const stores = memberships
      .map((m) => ({
        name: m.store.name,
        domain: m.store.domains[0]?.domain,
        role: m.role,
        organizationId: m.store.organizationId,
        organizationName: m.store.organization.name,
      }))
      .filter(
        (s): s is { name: string; domain: string; role: string; organizationId: string; organizationName: string } =>
          !!s.domain
      );

    return { stores };
  }

  /**
   * Refuses sign-in for a **deleted** merchant. Suspension is not checked here
   * on purpose — a suspended merchant keeps their access and sees a banner.
   *
   * The message is deliberately specific rather than a credentials error: a
   * closed account returning "invalid email or password" sends an ex-customer
   * into a password-reset loop they can never complete, and generates a support
   * ticket that says the login is broken.
   */
  private async assertOrganizationCanSignIn(organizationId?: string | null): Promise<void> {
    if (!organizationId) return;

    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { deletedAt: true },
    });

    if (org?.deletedAt) {
      throw new ForbiddenException('This account is closed. Please contact support.');
    }
  }

  async login(input: LoginRequest): Promise<AuthResponse> {
    const email = input.email?.trim().toLowerCase();
    const user = email ? await this.authRepository.findByEmail(email) : undefined;

    if (!user || !(await bcrypt.compare(input.password ?? '', user.passwordHash))) {
      throw new UnauthorizedException('Invalid email or password');
    }

    if (!user.emailVerifiedAt) {
      throw new ForbiddenException('Verify your email before logging in');
    }

    await this.assertOrganizationCanSignIn(user.organizationId);

    // Only meaningful for customers — this is how "joining" a new store
    // works: logging in at a store you haven't shopped at before, with the
    // correct password, is itself proof enough to add the membership. No
    // separate join/invite step. Safe to call for every role — a merchant/
    // platform account simply never gets a CustomerStoreMembership row
    // (nothing reads that table for them).
    if (user.role === 'customer') {
      await this.ensureMembership(user.id, this.ambientStoreId());
    }

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'login',
      changedBy: user.id,
      source: 'AuthService.login',
    });

    return await this.withSession(user);
  }

  /**
   * Builds the auth response and, where the session store is available, opens
   * a revocable session alongside it.
   *
   * Additive on purpose: `accessToken` is unchanged, so every existing client
   * keeps working untouched while the apps migrate one at a time. Only clients
   * that know to keep `refreshToken` gain the long-lived session.
   *
   * Failing soft matters here — a login must not break because the session
   * table is unreachable. The user simply gets the previous behaviour: an
   * access token with no refresh, i.e. exactly what they had before.
   */
  private async withSession(user: StoredUser): Promise<AuthResponse> {
    const response = await this.toAuthResponse(user);

    if (!this.sessionService) return response;

    try {
      return { ...response, refreshToken: await this.sessionService.issue(user.id) };
    } catch (error) {
      this.logger.warn(`Could not open a session for user ${user.id}: ${(error as Error).message}`);
      return response;
    }
  }

  /**
   * Exchanges a rotating refresh token for a new access token.
   *
   * Distinct from `refresh()` above, which re-signs a still-valid access token
   * and therefore cannot help once it has expired — the case that actually
   * logs people out. This one works precisely then, which is the whole point,
   * and SessionService.rotate() invalidates the presented token and detects
   * replay of an already-rotated one.
   */
  async refreshSession(refreshToken: string): Promise<AuthResponse> {
    if (!this.sessionService) {
      throw new UnauthorizedException('Sessions are not enabled');
    }

    const { userId, refreshToken: rotated } = await this.sessionService.rotate(refreshToken);
    const user = await this.authRepository.findById(userId);

    if (!user) throw new UnauthorizedException('Invalid session');

    return { ...(await this.toAuthResponse(user)), refreshToken: rotated };
  }

  /** Ends one session server-side — what logout should call. */
  async endSession(refreshToken: string): Promise<void> {
    await this.sessionService?.revoke(refreshToken, 'logout');
  }

  // Customer-only — this is the storefront's own Google button, distinct
  // from loginWithGoogleMerchant() below. Fixed after a live incident: this
  // used to call findByGoogleId()/findByEmail() with no role check, so a
  // real person who separately manages a store (a merchant/platform
  // account sharing the same Google identity) could sign in here and get
  // handed THAT account's session — a store_manager JWT landing on a
  // customer storefront. Both lookups now require role === 'customer';
  // anything else is treated as "no account here" and a fresh customer
  // account is created, same as if nothing had matched at all.
  async loginWithGoogle(profile: GoogleProfile): Promise<AuthResponse> {
    const byGoogleId = await this.authRepository.findByGoogleId(profile.googleId);
    let user = byGoogleId?.role === 'customer' ? byGoogleId : undefined;

    if (!user) {
      const byEmail = await this.authRepository.findByEmail(profile.email);
      const existing = byEmail?.role === 'customer' ? byEmail : undefined;
      user = existing
        ? await this.authRepository.linkGoogleId(existing.id, profile.googleId)
        : await this.authRepository.createWithGoogle(profile);
    }

    // Same "logging in here is the join" reasoning as login() above.
    await this.ensureMembership(user.id, this.ambientStoreId());

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'login',
      changedBy: user.id,
      source: 'AuthService.loginWithGoogle',
    });

    return await this.withSession(user);
  }

  // Merchant-only Google login — deliberately never auto-creates (gap 5 in
  // the plan doc): Google can't collect business details, so a merchant
  // account must already exist via registerMerchant() + email verification.
  // Distinct from loginWithGoogle() rather than a shared flag, so the
  // ordinary customer path can never accidentally pick up these checks.
  async loginWithGoogleMerchant(profile: GoogleProfile): Promise<AuthResponse> {
    const byGoogleId = await this.authRepository.findByGoogleId(profile.googleId);
    const user = byGoogleId ?? (await this.authRepository.findByEmail(profile.email));

    if (!user || user.role !== 'merchant') {
      throw new UnauthorizedException('No merchant account found for this Google account — register first');
    }

    if (!user.emailVerifiedAt) {
      throw new ForbiddenException('Verify your email before logging in');
    }

    await this.assertOrganizationCanSignIn(user.organizationId);

    const linked = byGoogleId ? user : await this.authRepository.linkGoogleId(user.id, profile.googleId);

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: linked.id,
      operation: 'login',
      changedBy: linked.id,
      source: 'AuthService.loginWithGoogleMerchant',
    });

    return await this.withSession(linked);
  }

  // Reveals whether the email is registered (404 vs success) — a deliberate
  // tradeoff requested over the no-enumeration default, so admins get clear
  // feedback instead of a silent no-op for unknown emails.
  async forgotPassword(input: ForgotPasswordRequest): Promise<void> {
    const email = input.email?.trim().toLowerCase();
    const user = email ? await this.authRepository.findByEmail(email) : undefined;

    if (!user) {
      throw new NotFoundException('No account found with that email');
    }

    const rawToken = randomBytes(32).toString('hex');
    const tokenHash = createHash('sha256').update(rawToken).digest('hex');
    const expiresAt = new Date(Date.now() + RESET_TOKEN_TTL_MS);

    await this.authRepository.setResetToken(user.id, tokenHash, expiresAt);

    const origin = resolveFrontendOrigin(input.returnOrigin);
    const resetUrl = `${origin}/reset-password?token=${rawToken}`;

    await this.emailService?.sendPasswordResetEmail(user.email, resetUrl);

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'password-reset-requested',
      changedBy: user.id,
      source: 'AuthService.forgotPassword',
    });
  }

  async resetPassword(input: ResetPasswordRequest): Promise<void> {
    if (!input.newPassword || input.newPassword.length < 8) {
      throw new BadRequestException('password must be at least 8 characters');
    }

    const tokenHash = createHash('sha256').update(input.token ?? '').digest('hex');
    const user = await this.authRepository.findByResetTokenHash(tokenHash);

    if (!user) {
      throw new BadRequestException('invalid or expired token');
    }

    const passwordHash = await bcrypt.hash(input.newPassword, 10);
    await this.authRepository.resetPassword(user.id, passwordHash);

    // Ending every other session is the point of a password reset. Someone
    // resetting a password has usually lost control of the account, and until
    // now the reset changed the password while leaving the attacker's existing
    // session running — the JWT was self-contained with no server-side record,
    // so nothing could invalidate it before its hour was up.
    //
    // Optional so this keeps working wherever AuthService is constructed
    // without the session store (several specs do exactly that).
    const revoked = (await this.sessionService?.revokeAllForUser(user.id, 'password-reset')) ?? 0;

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'password-reset',
      changedBy: user.id,
      source: `AuthService.resetPassword (${revoked} session(s) revoked)`,
    });

    await this.emailService?.sendTemplated('password_changed', user.email, {
      customerName: user.name,
    });
  }

  // Verifying logs the user straight in — re-fetched after markEmailVerified
  // so the returned AuthUser reflects the now-set emailVerifiedAt, not the
  // stale pre-verification copy findByEmailVerificationTokenHash returned.
  async verifyEmail(input: VerifyEmailRequest): Promise<AuthResponse> {
    const tokenHash = createHash('sha256').update(input.token ?? '').digest('hex');
    const user = await this.authRepository.findByEmailVerificationTokenHash(tokenHash);

    if (!user) {
      throw new BadRequestException('invalid or expired token');
    }

    await this.authRepository.markEmailVerified(user.id);

    // This is a customer's first real "login" — register() never issues a
    // session, so this is the earliest point a CustomerStoreMembership can
    // be created for them. Same role gate as login()/loginWithGoogle()
    // (this endpoint is shared with merchant verification too).
    if (user.role === 'customer') {
      await this.ensureMembership(user.id, this.ambientStoreId());
    }

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'email-verified',
      changedBy: user.id,
      source: 'AuthService.verifyEmail',
    });

    const verified = await this.authRepository.findById(user.id);
    return await this.withSession(verified ?? user);
  }

  // Same enumeration tradeoff as forgotPassword (404 on unknown email), but
  // an already-verified account is a silent no-op rather than an error —
  // there's nothing wrong with the request, just nothing left to do.
  async resendVerification(input: ResendVerificationRequest): Promise<void> {
    const email = input.email?.trim().toLowerCase();
    const user = email ? await this.authRepository.findByEmail(email) : undefined;

    if (!user) {
      throw new NotFoundException('No account found with that email');
    }

    if (user.emailVerifiedAt) {
      return;
    }

    await this.issueVerificationEmail(user, input.returnOrigin);
  }

  private async issueVerificationEmail(user: StoredUser, returnOrigin?: string): Promise<void> {
    const rawToken = randomBytes(32).toString('hex');
    const tokenHash = createHash('sha256').update(rawToken).digest('hex');
    const expiresAt = new Date(Date.now() + VERIFICATION_TOKEN_TTL_MS);

    await this.authRepository.setEmailVerificationToken(user.id, tokenHash, expiresAt);

    const origin = resolveFrontendOrigin(returnOrigin);
    const verificationUrl = `${origin}/verify-email?token=${rawToken}`;

    await this.emailService?.sendTemplated('email_verification', user.email, {
      customerName: user.name,
      verificationUrl,
    });
  }

  async me(accessToken: string): Promise<AuthUser> {
    const payload = this.verifyToken(accessToken);
    const user = await this.authRepository.findById(payload.sub);

    if (!user) {
      throw new UnauthorizedException('Invalid access token');
    }

    return this.toAuthUser(user);
  }

  // Shared with UsersService so every surface that returns a user profile
  // (register/login/me/refresh, and GET /users/me) exposes the same shape —
  // storeId and the role's resolved permissions included.
  async toAuthUser(user: StoredUser): Promise<AuthUser> {
    const permissions = await this.loadPermissions(user.role);
    const orgInfo = user.role === 'merchant' ? await this.loadOrganizationInfo(user.organizationId) : {};
    return { ...this.toPublicUser(user), permissions, ...orgInfo };
  }

  // Only called for role === 'merchant' — lets AdminAuthService.homeRoute()
  // redirect a freshly logged-in merchant straight to
  // /application-status/:organizationId off the existing GET /auth/me
  // response, no extra round trip.
  private async loadOrganizationInfo(
    organizationId?: string
  ): Promise<
    Pick<
      AuthUser,
      | 'organizationId'
      | 'organizationName'
      | 'organizationApprovalStatus'
      | 'organizationRejectionReason'
      | 'organizationSuspended'
      | 'organizationSuspensionReason'
    >
  > {
    if (!organizationId) {
      return {};
    }

    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: {
        name: true,
        approvalStatus: true,
        rejectionReason: true,
        suspendedAt: true,
        suspensionReason: true,
      },
    });

    if (!org) {
      return {};
    }

    return {
      organizationId,
      organizationName: org.name,
      organizationApprovalStatus: org.approvalStatus as MerchantApprovalStatus,
      organizationRejectionReason: org.rejectionReason ?? undefined,
      // Advisory only — a suspended merchant still signs in and works, they
      // just see a banner. Blocking is reserved for deleted accounts, and that
      // happens at login (see assertOrganizationCanSignIn), not here.
      organizationSuspended: org.suspendedAt !== null,
      organizationSuspensionReason: org.suspensionReason ?? undefined,
    };
  }

  // Public — deliberately no auth required. Backs the shareable
  // /application-status/:id admin page, which must work whether or not the
  // viewer's session is still active (that's the whole point of it being a
  // link, not a session-only view). Only non-sensitive fields are exposed —
  // no email/phone/gstin — same posture as any other "check my order
  // status" style public lookup.
  async getApplicationStatus(organizationId: string): Promise<ApplicationStatusResponse> {
    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { id: true, name: true, approvalStatus: true, rejectionReason: true, plan: true },
    });

    if (!org) {
      throw new NotFoundException('No application found for this link');
    }

    return {
      organizationId: org.id,
      businessName: org.name,
      approvalStatus: org.approvalStatus as MerchantApprovalStatus,
      rejectionReason: org.rejectionReason ?? undefined,
      plan: org.plan as MerchantPlan,
    };
  }

  async refresh(accessToken: string): Promise<AuthResponse> {
    const payload = this.verifyToken(accessToken);
    const user = await this.authRepository.findById(payload.sub);

    if (!user) {
      throw new UnauthorizedException('Invalid access token');
    }

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'refresh',
      changedBy: user.id,
      source: 'AuthService.refresh',
    });

    return await this.toAuthResponse(user);
  }

  verifyToken(accessToken: string): AuthTokenPayload {
    try {
      return jwt.verify(accessToken, this.jwtSecret) as AuthTokenPayload;
    } catch {
      throw new UnauthorizedException('Invalid access token');
    }
  }

  private async loadPermissions(role: string): Promise<string[]> {
    try {
      const platformRole = await this.prisma.platformRole.findUnique({
        where: { code: role },
        select: {
          rolePermissions: {
            where: { isActive: true },
            select: { permission: { select: { code: true } } },
          },
        },
      });
      return platformRole?.rolePermissions.map((rp) => rp.permission.code) ?? [];
    } catch {
      return [];
    }
  }

  private validateRegister(input: RegisterRequest): void {
    if (!input.email?.trim() || !input.email.includes('@')) {
      throw new BadRequestException('valid email is required');
    }

    if (!input.name?.trim()) {
      throw new BadRequestException('name is required');
    }

    if (!input.password || input.password.length < 8) {
      throw new BadRequestException('password must be at least 8 characters');
    }
  }

  private validateRegisterMerchant(input: RegisterMerchantRequest): void {
    if (!input.email?.trim() || !input.email.includes('@')) {
      throw new BadRequestException('valid email is required');
    }

    if (!input.name?.trim()) {
      throw new BadRequestException('name is required');
    }

    if (!input.password || input.password.length < 8) {
      throw new BadRequestException('password must be at least 8 characters');
    }

    if (!input.businessName?.trim()) {
      throw new BadRequestException('business name is required');
    }

    if (!input.businessType?.trim()) {
      throw new BadRequestException('business type is required');
    }

    if (!input.phone?.trim()) {
      throw new BadRequestException('phone is required');
    }

    if (!['free', 'basic', 'premium'].includes(input.plan)) {
      throw new BadRequestException('a valid plan is required');
    }
  }

  private async uniqueOrgSlug(name: string): Promise<string> {
    const base = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'merchant';

    let attempt = 0;
    for (;;) {
      const candidate = attempt === 0 ? base : `${base}-${attempt}`;
      const existing = await this.prisma.organization.findUnique({
        where: { slug: candidate },
        select: { id: true },
      });
      if (!existing) return candidate;
      attempt++;
    }
  }

  /**
   * Best-effort first store for a new merchant.
   *
   * Never throws into registration: see the call site for why an account
   * without a store beats no account at all.
   */
  private async provisionFirstStore(
    organizationId: string,
    userId: string,
    input: RegisterMerchantRequest
  ): Promise<void> {
    if (!this.storeProvisioning) return;

    try {
      const name = input.businessName.trim();
      const subdomain = await this.storeProvisioning.deriveSubdomain(name);

      if (!subdomain) {
        this.logger.warn(`could not derive a free subdomain for "${name}" — merchant will pick one`);
        return;
      }

      await this.storeProvisioning.provision({
        organizationId,
        userId,
        name,
        subdomain,
        slug: await this.storeProvisioning.uniqueSlug(name, organizationId),
        city: input.city?.trim() || null,
        state: input.state?.trim() || null,
        createdBy: 'system:merchant-registration',
      });
    } catch (error) {
      this.logger.error(
        `first-store provisioning failed for org ${organizationId}: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  private async toAuthResponse(user: StoredUser): Promise<AuthResponse> {
    return {
      user: await this.toAuthUser(user),
      accessToken: jwt.sign(
        // name/storeId ride along so downstream services (chat-service's
        // AgentJwtGuard) can authorize and attribute agents without a
        // callback to this API. storeId is omitted for customers — identity
        // is global (CustomerStoreMembership), so there's no single store a
        // customer token could correctly claim to belong to.
        {
          sub: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
          ...(user.role === 'customer' ? {} : { storeId: user.storeId }),
        },
        this.jwtSecret,
        { expiresIn: ACCESS_TOKEN_TTL as jwt.SignOptions['expiresIn'] }
      ),
    };
  }

  private toPublicUser(user: StoredUser): AuthUser {
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      storeId: user.storeId,
      themeMode: user.themeMode,
      createdAt: user.createdAt,
      emailVerifiedAt: user.emailVerifiedAt,
    };
  }
}
